import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Receipt, FileText, Users, Package,
  TrendingUp, Settings, LogOut, PanelLeftClose, PanelLeftOpen,
  BookOpen, Library, ShieldCheck, Landmark, Building2, UserCog,
  ClipboardList, Repeat, Menu, FolderKanban, Plane, FileBarChart, Briefcase, Archive, Boxes, Smartphone, Clock, Bell, Target, ShoppingCart, KeyRound,
  HeartHandshake, UsersRound, Gift, CalendarDays, Send, Banknote,
  PieChart, FileUp, Sparkles,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const MENY = [
  { path: '/',              icon: LayoutDashboard, label: 'Oversikt' },
];

const GRUPPER = [
  {
    tittel: 'Daglig drift',
    poster: [
      { path: '/bilag',       icon: Receipt,         label: 'Bilag' },
      { path: '/innkjop',     icon: ShoppingCart,    label: 'Innkjøp' },
      { path: '/faktura',     icon: FileText,        label: 'Fakturaer' },
      { path: '/tilbud',      icon: ClipboardList,   label: 'Tilbud' },
      { path: '/purring',     icon: Bell,            label: 'Purring' },
      { path: '/repeterende', icon: Repeat,          label: 'Abonnement' },
    ],
  },
  {
    tittel: 'Personale',
    poster: [
      { path: '/timer', icon: Clock,     label: 'Timeføring' },
      { path: '/lonn',  icon: Briefcase, label: 'Lønn' },
      { path: '/reise', icon: Plane,     label: 'Reise' },
    ],
  },
  {
    tittel: 'Katalog',
    poster: [
      { path: '/kontakter',  icon: Users,        label: 'Kontakter' },
      { path: '/produkter',  icon: Package,      label: 'Produkter' },
      { path: '/lager',      icon: Boxes,        label: 'Lager' },
      { path: '/prosjekter', icon: FolderKanban, label: 'Prosjekter' },
    ],
  },
  {
    tittel: 'Bank & bokføring',
    poster: [
      { path: '/bank',          icon: Landmark,     label: 'Bank' },
      { path: '/bokforing',     icon: BookOpen,     label: 'Bokføring' },
      { path: '/kontoplan',     icon: Library,      label: 'Kontoplan' },
      { path: '/anleggsmidler', icon: Building2,    label: 'Anleggsmidler' },
      { path: '/mva',           icon: FileBarChart, label: 'MVA-melding' },
      { path: '/skatt-aga',     icon: Landmark,     label: 'Skatt & AGA' },
    ],
  },
  {
    tittel: 'Forening',
    krevForening: true,
    poster: [
      { path: '/medlemmer', icon: HeartHandshake, label: 'Medlemmer', krevForening: true },
      { path: '/grupper',   icon: UsersRound,     label: 'Grupper & verv', krevForening: true },
      { path: '/innsamling',icon: Gift,           label: 'Innsamling', krevForening: true },
      { path: '/arrangement',  icon: CalendarDays, label: 'Arrangement', krevForening: true },
      { path: '/kommunikasjon',icon: Send,         label: 'Kommunikasjon', krevForening: true },
      { path: '/avtalegiro-ocr',  icon: Banknote,     label: 'AvtaleGiro & OCR', krevForening: true },
    ],
  },
  {
    tittel: 'Eierskap & finans',
    poster: [
      { path: '/aksjeeierbok', icon: UsersRound,   label: 'Aksjeeierbok' },
      { path: '/gjeld',        icon: Landmark,     label: 'Gjeld' },
      { path: '/verdipapirer', icon: PieChart,     label: 'Verdipapirer' },
    ],
  },
  {
    tittel: 'Analyse',
    poster: [
      { path: '/rapport',  icon: TrendingUp, label: 'Rapporter' },
      { path: '/budsjett', icon: Target,     label: 'Budsjett' },
    ],
  },
  {
    tittel: 'System',
    poster: [
      { path: '/tjenester',     icon: Sparkles,    label: 'Tjenester' },
      { path: '/compliance',    icon: ShieldCheck, label: 'Compliance' },
      { path: '/dokumenter',    icon: Archive,     label: 'Arkiv' },
      { path: '/import-eksport',icon: FileUp,      label: 'Import / eksport' },
      { path: '/brukere',       icon: UserCog,     label: 'Brukere' },
      { path: '/innstillinger', icon: Settings,    label: 'Innstillinger' },
      { path: '/admin',         icon: KeyRound,    label: 'Admin', krevSuperadmin: true },
    ],
  },
];

const useErMobil = () => {
  const [erMobil, setErMobil] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 900 : false
  );
  useEffect(() => {
    const onResize = () => setErMobil(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return erMobil;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, firma, logout, refresh } = useAuth();
  const { tema } = useTheme();
  const erMorkt = tema !== 'light';
  const logoBred = erMorkt ? '/logo_4.png' : '/logo.png';
  const logoIkon = '/logo_2.png';
  const erMobil = useErMobil();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar_collapsed') === 'true'
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [varsler, setVarsler] = useState({ antall: 0, innboks_antall: 0, items: [] });
  const [varslerOpen, setVarslerOpen] = useState(false);

  // Lukk drawer ved navigering
  useEffect(() => { setDrawerOpen(false); setVarslerOpen(false); }, [location.pathname]);

  // Hent varsler hver 60s
  useEffect(() => {
    let avbrutt = false;
    const hent = () => {
      api.get('/varsler').then(r => { if (!avbrutt) setVarsler(r.data); }).catch(() => {});
    };
    hent();
    const t = setInterval(hent, 60000);
    return () => { avbrutt = true; clearInterval(t); };
  }, []);

  const toggle = () => {
    const v = !collapsed;
    setCollapsed(v);
    localStorage.setItem('sidebar_collapsed', String(v));
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const byttTilSimple = async () => {
    if (!window.confirm('Bytte til mobil/skann-modus? Du får kun tilgang til skann-siden – kan endres tilbake senere.')) return;
    try {
      await api.post('/auth/visning', { visning_modus: 'simple' });
      await refresh();
      navigate('/skann');
    } catch (e) { alert('Kunne ikke bytte modus.'); }
  };

  const sideWidth = erMobil ? 240 : (collapsed ? 76 : 220);
  const sidebarSynlig = !erMobil || drawerOpen;

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      background: 'var(--theme-bg-primary)',
      position: 'relative',
    }}>
      {/* Mobil-overlay */}
      {erMobil && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 998,
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className="fm-bg-sidebar no-print"
        style={{
          width: sideWidth,
          minWidth: sideWidth,
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s, width 0.2s',
          borderRight: '1px solid var(--theme-border)',
          position: erMobil ? 'fixed' : 'relative',
          top: 0, bottom: 0, left: 0,
          zIndex: 999,
          transform: sidebarSynlig ? 'translateX(0)' : 'translateX(-100%)',
        }}
      >
        <div className="flex items-center justify-between"
             style={{ padding: collapsed && !erMobil ? '14px 8px' : '14px 16px',
                      borderBottom: '1px solid var(--theme-border)',
                      gap: 8, flexDirection: collapsed && !erMobil ? 'column' : 'row' }}>
          <div className="flex items-center" style={{ gap: 8, minWidth: 0, flex: 1, justifyContent: collapsed && !erMobil ? 'center' : 'flex-start' }}>
            {(collapsed && !erMobil) ? (
              <img src={logoIkon} alt="REGNINO" style={{ height: 36, width: 36, objectFit: 'contain' }} />
            ) : (
              <img src={logoBred} alt="REGNINO" style={{ height: 32, width: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
            )}
          </div>
          {!erMobil && (
            <button className="btn-ghost" onClick={toggle}
                    style={{ height: 32, padding: '0 8px', border: 'none' }}
                    title={collapsed ? 'Utvid' : 'Skjul'}>
              {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
            </button>
          )}
        </div>

        {/* Varslingspanel-knapp */}
        {!erMobil && !collapsed && (
          <div style={{ position: 'relative', padding: '8px 12px 0' }}>
            <button onClick={() => setVarslerOpen(v => !v)} className="btn-ghost"
                    style={{ width: '100%', justifyContent: 'space-between',
                             border: '1px solid var(--theme-border)', height: 32 }}
                    title="Varsler">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <Bell size={14} /> Varsler
              </span>
              {varsler.antall > 0 && (
                <span style={{
                  minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
                  background: '#ef4444', color: '#fff',
                  fontSize: 10, fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>{varsler.antall > 99 ? '99+' : varsler.antall}</span>
              )}
            </button>
            {varslerOpen && (
              <div style={{
                position: 'absolute', top: 44, left: 12, right: 12, zIndex: 1100,
                background: 'var(--theme-bg-card)',
                border: '1px solid var(--theme-border)',
                borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.18)',
                maxHeight: 360, overflowY: 'auto',
              }}>
                {varsler.items.length === 0 && (
                  <div className="fm-text-muted" style={{ padding: 14, fontSize: 12, textAlign: 'center' }}>
                    Ingen nye varsler.
                  </div>
                )}
                {varsler.items.map((v, idx) => (
                  <Link key={idx} to={v.url} onClick={() => setVarslerOpen(false)}
                        style={{
                          display: 'block', padding: '10px 12px', textDecoration: 'none',
                          color: 'var(--theme-text)',
                          borderBottom: idx < varsler.items.length - 1 ? '1px solid var(--theme-border)' : 'none',
                          background: v.alvorlighet === 'advarsel' ? 'rgba(239,68,68,0.06)' : 'transparent',
                        }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{v.tittel}</div>
                    <div className="fm-text-muted" style={{ fontSize: 11 }}>{v.tekst}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        <nav style={{ flex: 1, padding: 8, overflowY: 'auto' }}>
          {[...MENY, ...GRUPPER.filter(g => !g.krevForening || firma?.er_forening).flatMap(g => [{ _gruppe: g.tittel }, ...g.poster])]
            .filter(m => (!m.krevEier || user?.er_eier) && (!m.krevSuperadmin || user?.er_superadmin) && (!m.krevForening || firma?.er_forening))
            .map((m, idx) => {
            const visKollapsert = collapsed && !erMobil;
            if (m._gruppe) {
              if (visKollapsert) {
                return <div key={`g-${idx}`} style={{
                  height: 1, margin: '8px 8px', background: 'var(--theme-border)', opacity: 0.4,
                }} />;
              }
              return (
                <div key={`g-${idx}`} style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'var(--theme-text-secondary)',
                  padding: '14px 12px 4px', opacity: 0.65,
                }}>{m._gruppe}</div>
              );
            }
            const Icon = m.icon;
            const aktiv = m.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(m.path);
            const badge = m.path === '/bilag' ? varsler.innboks_antall
                        : m.path === '/purring' ? (varsler.items.find(i => i.type === 'purring')?.antall || 0)
                        : 0;
            return (
              <Link
                key={m.path}
                to={m.path}
                title={m.label + (badge ? ` (${badge} nye)` : '')}
                style={{
                  display: 'flex', alignItems: 'center',
                  gap: 12, padding: visKollapsert ? '12px' : '8px 12px',
                  marginBottom: 1, borderRadius: 6,
                  textDecoration: 'none',
                  background: aktiv ? 'var(--theme-accent)' : 'transparent',
                  color: aktiv ? '#fff' : 'var(--theme-text-secondary)',
                  fontSize: erMobil ? 14 : 13, fontWeight: 500,
                  justifyContent: visKollapsert ? 'center' : 'flex-start',
                  minHeight: erMobil ? 44 : 'auto',
                  position: 'relative',
                }}
                onMouseEnter={(e) => { if (!aktiv) e.currentTarget.style.background = 'var(--theme-bg-hover)'; }}
                onMouseLeave={(e) => { if (!aktiv) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <Icon size={erMobil ? 18 : 16} />
                  {badge > 0 && (
                    <span style={{
                      position: 'absolute', top: -6, right: -8,
                      minWidth: 16, height: 16, padding: '0 4px',
                      borderRadius: 8, background: '#ef4444', color: '#fff',
                      fontSize: 10, fontWeight: 700,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      border: '2px solid var(--theme-bg-card)',
                    }}>{badge > 99 ? '99+' : badge}</span>
                  )}
                </span>
                {!visKollapsert && <span style={{ flex: 1 }}>{m.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div style={{ padding: 12, borderTop: '1px solid var(--theme-border)' }}>
          {(!collapsed || erMobil) && user && (
            <div style={{ marginBottom: 8 }}>
              <div className="fm-text-primary" style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.navn || user.epost}
              </div>
              {user.navn && (
                <div className="fm-text-muted" style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.epost}
                </div>
              )}
            </div>
          )}
          <button
            onClick={byttTilSimple}
            className="btn-ghost"
            style={{
              width: '100%',
              justifyContent: (collapsed && !erMobil) ? 'center' : 'flex-start',
              border: 'none',
              minHeight: erMobil ? 40 : 'auto',
              marginBottom: 4,
            }}
            title="Bytt til mobil-modus (skann)"
          >
            <Smartphone size={(collapsed && !erMobil) ? 18 : 14} />
            {(!collapsed || erMobil) && <span>Mobil-modus</span>}
          </button>
          <button
            onClick={handleLogout}
            className="btn-ghost"
            style={{
              width: '100%',
              justifyContent: (collapsed && !erMobil) ? 'center' : 'flex-start',
              color: 'var(--theme-danger)',
              border: 'none',
              minHeight: erMobil ? 40 : 'auto',
            }}
            title="Logg ut"
          >
            <LogOut size={(collapsed && !erMobil) ? 18 : 14} />
            {(!collapsed || erMobil) && <span>Logg ut</span>}
          </button>
        </div>
      </aside>

      {/* Hovedkolonne */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {erMobil && (
          <div className="no-print"
               style={{
                 display: 'flex', alignItems: 'center', gap: 10,
                 padding: '10px 14px',
                 borderBottom: '1px solid var(--theme-border)',
                 background: 'var(--theme-bg-secondary)',
               }}>
            <button className="btn-ghost" onClick={() => setDrawerOpen(true)}
                    style={{ height: 36, padding: '0 10px' }} title="Meny">
              <Menu size={18} />
            </button>
            <div className="flex items-center" style={{ gap: 8, minWidth: 0 }}>
              <img src={logoBred} alt="REGNINO" style={{ height: 28, width: 'auto', maxWidth: 160, objectFit: 'contain' }} />
            </div>
          </div>
        )}
        <main style={{
          flex: 1, overflow: 'auto',
          padding: erMobil ? '14px 14px 24px' : '20px 28px',
        }}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
